import React, { useEffect, useState } from 'react';
import { getSeedReports, getAllAnalyses } from '../utils/api';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';
import './AnalysisPage.css';

const avg = (nums) => nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;

export default function TeamAnalysisPage() {
  const [reports, setReports] = useState([]);

  useEffect(() => {
    Promise.all([getSeedReports(), getAllAnalyses().catch(() => ({ data: [] }))])
      .then(([seed, mine]) => {
        const userReports = (mine.data || []).filter(r => r.status === 'complete' && r.report);
        setReports([...userReports, ...(seed.data || [])]);
      });
  }, []);

  // Everything below is computed from the actual reports loaded above — no
  // placeholder numbers. "home" is treated as the analysed side's own team,
  // matching the convention used on each individual report's own radar chart.
  const withReport = reports.filter(r => r.report);

  const radarData = [
    { metric: 'Press',       home: avg(withReport.map(r => r.report.pressingAnalysis?.home?.intensity).filter(v => v != null)) * 10 || 0,
                              away: avg(withReport.map(r => r.report.pressingAnalysis?.away?.intensity).filter(v => v != null)) * 10 || 0 },
    { metric: 'Possession',  home: avg(withReport.map(r => r.report.possession?.home).filter(v => v != null)) || 0,
                              away: avg(withReport.map(r => r.report.possession?.away).filter(v => v != null)) || 0 },
    { metric: 'Defense',     home: avg(withReport.map(r => r.report.defensiveStructure?.home?.compactness).filter(v => v != null)) * 10 || 0,
                              away: avg(withReport.map(r => r.report.defensiveStructure?.away?.compactness).filter(v => v != null)) * 10 || 0 },
    { metric: 'Attack',      home: Math.min(100, (avg(withReport.map(r => r.report.attackingPatterns?.home?.chancesCreated).filter(v => v != null)) || 0) * 5),
                              away: Math.min(100, (avg(withReport.map(r => r.report.attackingPatterns?.away?.chancesCreated).filter(v => v != null)) || 0) * 5) },
    { metric: 'Pass Acc.',   home: avg(withReport.map(r => r.report.keyStats?.passAccuracy?.home).filter(v => v != null)) || 0,
                              away: avg(withReport.map(r => r.report.keyStats?.passAccuracy?.away).filter(v => v != null)) || 0 },
  ];

  const formData = withReport
    .slice()
    .sort((a, b) => new Date(a.uploadedAt || a.matchDate) - new Date(b.uploadedAt || b.matchDate))
    .slice(-8)
    .map(r => ({ match: `vs ${r.awayTeam}`, score: r.report.analysisConfidence ?? 0 }));

  const avgPossession  = avg(withReport.map(r => r.report.possession?.home).filter(v => v != null));
  const avgPressIntens = avg(withReport.map(r => r.report.pressingAnalysis?.home?.intensity).filter(v => v != null));
  const avgPassAcc     = avg(withReport.map(r => r.report.keyStats?.passAccuracy?.home).filter(v => v != null));
  const avgConfidence  = avg(withReport.map(r => r.report.analysisConfidence).filter(v => v != null));

  return (
    <div className="analysis-page fade-in">
      <div className="analysis-page__header">
        <div>
          <div className="dashboard__eyebrow">Season Overview</div>
          <h1>Team Analysis</h1>
          <p>Aggregated performance across all {reports.length} analysed matches — computed live from your reports, nothing hardcoded.</p>
        </div>
      </div>

      <div className="analysis-grid stagger">
        <div className="card analysis-section">
          <h3 className="analysis-section__title">Performance Radar</h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData} cx="50%" cy="50%" outerRadius={100}>
              <PolarGrid stroke="#1f2d45" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <Radar name="Your Team" dataKey="home" stroke="#00d4ff" fill="#00d4ff" fillOpacity={0.2} />
              <Radar name="Avg Opposition" dataKey="away" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15} />
              <Tooltip contentStyle={{ background: '#1a2236', border: '1px solid #1f2d45', borderRadius: 8, fontSize: 12 }} />
            </RadarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8' }}>
              <div style={{ width: 12, height: 3, background: '#00d4ff', borderRadius: 2 }} /> Your Team
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8' }}>
              <div style={{ width: 12, height: 3, background: '#7c3aed', borderRadius: 2 }} /> Avg Opposition
            </div>
          </div>
        </div>

        <div className="card analysis-section">
          <h3 className="analysis-section__title">Form Index</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={formData}>
              <XAxis dataKey="match" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1a2236', border: '1px solid #1f2d45', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="score" name="Performance Score" fill="#00d4ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card analysis-section analysis-section--full">
          <h3 className="analysis-section__title">Season Summary</h3>
          <div className="summary-kpis">
            {[
              { label: 'Matches Analysed',   value: reports.length, sub: 'Total' },
              { label: 'Avg Possession',     value: avgPossession  != null ? `${Math.round(avgPossession)}%` : '—', sub: 'Across analysed matches' },
              { label: 'Avg Press Intensity',value: avgPressIntens != null ? `${avgPressIntens.toFixed(1)}/10` : '—', sub: 'Across analysed matches' },
              { label: 'Avg Pass Accuracy',  value: avgPassAcc     != null ? `${Math.round(avgPassAcc)}%` : '—', sub: 'Across analysed matches' },
              { label: 'AI Confidence',      value: avgConfidence  != null ? `${Math.round(avgConfidence)}%` : '—', sub: 'Analysis accuracy' },
            ].map(k => (
              <div key={k.label} className="summary-kpi">
                <div className="summary-kpi__value">{k.value}</div>
                <div className="summary-kpi__label">{k.label}</div>
                <div className="summary-kpi__sub">{k.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {reports[0]?.report && (
          <div className="card analysis-section analysis-section--full">
            <h3 className="analysis-section__title">Latest Match — Tactical Breakdown</h3>
            <div className="tactical-breakdown">
              {reports[0].report.tacticalStrengths?.slice(0, 3).map((s, i) => (
                <div key={i} className="tactic-item">
                  <div className="tactic-item__header">
                    <span className="tactic-item__team">{s.team === 'home' ? reports[0].homeTeam : reports[0].awayTeam}</span>
                    <div className="tactic-item__rating">{s.rating}/10</div>
                  </div>
                  <div className="tactic-item__title">{s.title}</div>
                  <p className="tactic-item__desc">{s.description}</p>
                  <div className="progress-bar" style={{ marginTop: 10 }}>
                    <div className="progress-fill" style={{ width: `${s.rating * 10}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
